/**
 * BackupDeviceStep Component
 *
 * Step 2 of enrollment: Register an optional backup device.
 * This step is SKIPPABLE - user can proceed without backup.
 *
 * FERP-PII-052: Backup credential for account recovery.
 *
 * Note: Backup device registration does NOT generate new PRF output.
 * The backup device uses standard WebAuthn for authentication only.
 * L1/L1G data is still encrypted with the primary credential's PRF.
 */

import React, { useState } from 'react';
import {
  Stack,
  Text,
  TextInput,
  Button,
  Alert,
  Group,
  List,
  ThemeIcon,
  Progress,
  Box,
} from '@mantine/core';
import {
  IconDeviceMobile,
  IconCheck,
  IconAlertCircle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useAuth } from '../../auth/AuthContext';
import { webAuthnRepository } from '@ferp/workflow-forms';

export interface BackupDeviceStepProps {
  /** Called when step is complete */
  onComplete: (credentialId?: string, prfOutput?: string, deviceName?: string) => void;

  /** Called when user skips this step */
  onSkip: () => void;

  /** Loading state from parent */
  loading: boolean;
}

export const BackupDeviceStep: React.FC<BackupDeviceStepProps> = ({
  onComplete,
  onSkip,
  loading,
}) => {
  const { user, tenant } = useAuth();
  const [deviceName, setDeviceName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle backup device registration
   *
   * Note: Backup devices use standard biometric registration (no PRF).
   * They can authenticate the user but cannot decrypt L1/L1G data directly.
   * The primary credential's PRF output is used for encryption.
   */
  const handleRegister = async () => {
    if (!user?.id || !tenant?.id) {
      setError('User or tenant context not available. Please log in again.');
      return;
    }

    setRegistering(true);
    setError(null);

    try {
      // Backup device uses standard biometric registration (no PRF)
      const success = await webAuthnRepository.registerBiometric(
        user.id,
        user.email,
        user.displayName,
        tenant.id,
        deviceName.trim() || 'Backup Device'
      );

      if (!success) {
        throw new Error('Backup device registration failed');
      }

      // Backup device doesn't generate PRF output - pass undefined
      onComplete(
        `backup-${Date.now()}`, // Temporary ID, backend generates real one
        undefined, // No PRF output for backup device
        deviceName.trim() || 'Backup Device'
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
    } finally {
      setRegistering(false);
    }
  };

  return (
    <Stack gap="md" mt="md">
      {/* Info about backup devices */}
      <Alert color="blue" icon={<IconInfoCircle size={16} />}>
        <Text fw={500}>Why Add a Backup Device?</Text>
        <Text size="sm" mt={4}>
          If you lose access to your primary device, a backup device ensures you can still access
          your protected information.
        </Text>
      </Alert>

      {/* Recommendations */}
      <Alert color="gray" variant="light" title="Recommended Backup Options">
        <List size="sm" spacing="xs" mt="xs">
          <List.Item
            icon={
              <ThemeIcon color="gray" size={20} radius="xl" variant="light">
                <IconDeviceMobile size={12} />
              </ThemeIcon>
            }
          >
            A second phone or tablet
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="gray" size={20} radius="xl" variant="light">
                <IconDeviceMobile size={12} />
              </ThemeIcon>
            }
          >
            A work computer with Windows Hello or Touch ID
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="gray" size={20} radius="xl" variant="light">
                <IconDeviceMobile size={12} />
              </ThemeIcon>
            }
          >
            A hardware security key (YubiKey, etc.)
          </List.Item>
        </List>
      </Alert>

      {/* Device name input */}
      <TextInput
        label="Device Name"
        placeholder="Work Laptop"
        value={deviceName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeviceName(e.target.value)}
        description="Give this backup device a recognizable name"
      />

      {/* Error display */}
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Registration Failed">
          {error}
        </Alert>
      )}

      {/* Registration progress */}
      {registering && (
        <Box>
          <Text size="sm" mb="xs">
            Waiting for biometric scan on backup device...
          </Text>
          <Progress value={100} animated />
        </Box>
      )}

      {/* Action buttons */}
      <Group justify="space-between" mt="md">
        <Button variant="subtle" onClick={onSkip} disabled={registering || loading}>
          Skip This Step
        </Button>
        <Button
          onClick={handleRegister}
          loading={registering || loading}
          leftSection={<IconDeviceMobile size={18} />}
        >
          Add Backup Device
        </Button>
      </Group>
    </Stack>
  );
};
